import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Form as FinalForm, FormSpy } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import useTranslate from '../../../i18n/useTranslate'
import MyCheckbox from '../../../control/checkbox/checkbox'
import useAuthenStore from '../../../stores/authen/authenStore'
import Radio from '../../../control/radio/radio'
import MyTextfield from '../../../control/textfield/textfield'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Button from '@material-ui/core/Button'
import { useHistory, useParams } from 'react-router-dom'
import { RoutePath } from '../../../router/routesConfig'
import { styled } from '@material-ui/styles'
import { FieldArray } from 'react-final-form-arrays'
import Autocomplete from '../../../control/autocomplete/autocomplete'
import useLookupService from '../../../services/lookup/useLookupService'
import useExpertises from '../shareLogic/useExpertises'
import { ExpertiseFaculty } from '../../../services/lookup/types/responseLookupServiceTypes'
import useLoading from '../../../control/loading/useLoading'
import { FormApi } from 'final-form'
import Paper from '@material-ui/core/Paper'
import { useCommitteeStyles } from './committeeStyles'
import { TOptions } from '../../../control/autocomplete/autocompleteType'
import useCommittees from '../shareLogic/useCommittees'
import Dialog from '../../../control/dialog/dialog'
import { ServiceResult } from '../../../services/web/types/responseWebServiceTypes'

const CustomHeader = styled(Typography)(() => ({
    fontWeight: 'bold',
}))

interface CommitteeFormProps {
    isReadOnly?: boolean
}

const CommitteeForm: FC<CommitteeFormProps> = (props) => {
    const { isReadOnly } = props

    const [initialEditAble, setInitialEditAble] = useState<Record<string, any>>({
        faculty: null,
        expertList: [],
    })
    const translate = useTranslate()
    const authenStore = useAuthenStore()
    const updatedUserId = authenStore.profile.userId
    const params = useParams<{ id: string }>()
    const history = useHistory()
    const lutHook = useLookupService()
    const expertiseHook = useExpertises()
    const [initFaculty, setInitFaculty] = useState<ExpertiseFaculty>()
    const [initExpertise, setInitExpertise] = useState<Record<string, any>>()
    const loadingHook = useLoading()
    const [disabledAdd, setDisabledAdd] = useState(true)
    const classes = useCommitteeStyles()
    const committeeHook = useCommittees()
    const [committeeInfo, setCommitteeInfo] = useState()
    const cancelRef = useRef<Record<string, Function>>()
    const saveRef = useRef<Record<string, Function>>()
    const successRef = useRef<Record<string, Function>>()
    const formStateRef = useRef<FormApi<any, Partial<any>>>()

    useEffect(() => {
        initLut()
        initialForm()
    }, [])

    const getLut = (rawData: any, languageKey: string, enKey: string, thKey: string, idKey: string) => {
        const result = rawData
        return result?.map((data: any) => ({
            label: languageKey === translate.languageKey.en ? data[enKey] : data[thKey],
            value: `${data[idKey]}`,
        }))
    }
    const expertiseFaculty = useMemo(
        () => getLut(initFaculty?.result, translate.languageStore.currentLanguage, 'nameEN', 'nameTH', 'id'),
        [initFaculty, translate.languageStore.currentLanguage],
    )
    const expertise = useMemo(
        () => getLut(initExpertise?.result, translate.languageStore.currentLanguage, 'nameEN', 'nameTH', 'id'),
        [initExpertise, translate.languageStore.currentLanguage],
    )
    const initialValues = useMemo(() => {
        const isEN = translate.languageStore.currentLanguage === translate.languageKey.en

        return {
            usergroup: isEN ? committeeInfo?.['usergroupNameEN'] : committeeInfo?.['usergroupNameTH'],
            irb: isEN ? committeeInfo?.['irbNameEN'] : committeeInfo?.['irbNameTH'],
            isBothIrb: committeeInfo?.['isBothIrb'],
            cunet: committeeInfo?.['cunet'] ? '1' : '2',
            username: committeeInfo?.['username'],
            email: committeeInfo?.['email'],
            position: isEN ? committeeInfo?.['positionNameEN'] : committeeInfo?.['positionNameTH'],
            positionOtherTH: committeeInfo?.['position']['name']['th'],
            positionOtherEN: committeeInfo?.['position']['name']['en'],
            isPhD: committeeInfo?.['isPhD'],
            title: isEN ? committeeInfo?.['titleNameEN'] : committeeInfo?.['titleNameTH'],
            titleOtherTH: committeeInfo?.['title']['name']['en'],
            titleOtherEN: committeeInfo?.['title']['name']['en'],
            nameTH: committeeInfo?.['nameTH'],
            nameEN: committeeInfo?.['nameEN'],
            homeTelephone: committeeInfo?.['homeTelephone'],
            mobilePhone: committeeInfo?.['mobilePhone'],
        }
    }, [committeeInfo, translate.languageStore.currentLanguage])

    const initialEditableForm = useMemo(() => {
        const facultyVal = formStateRef.current?.getFieldState('faculty')?.value ?? null
        const expertVal = formStateRef.current?.getFieldState('expertList')?.value ?? []
        const isEN = translate.languageStore.currentLanguage === translate.languageKey.en
        let returnRes: { faculty: TOptions | null; expertList: TOptions[] } = {
            faculty: null,
            expertList: [],
        }
        if (facultyVal) {
            returnRes.faculty =
                expertiseFaculty.find((data: TOptions) => `${data.value}` === `${facultyVal.value}`) ?? null
        } else if (initialEditAble.faculty) {
            returnRes.faculty = {
                value: initialEditAble.faculty.id,
                label: isEN ? initialEditAble.faculty.nameEN : initialEditAble.faculty.nameTH,
            }
        }
        if (expertVal.length > 0) {
            returnRes.expertList = expertVal.map(
                (data: TOptions) =>
                    expertise.find((expertData: TOptions) => `${expertData.value}` === `${data.value}`) ?? null,
            )
        } else if (initialEditAble.expertList.length > 0) {
            returnRes.expertList = initialEditAble.expertList.map(
                (data: { id: string; nameTH: string; nameEN: string }) => ({
                    value: data.id,
                    label: isEN ? data.nameEN : data.nameTH,
                }),
            )
        }
        return returnRes
    }, [initialEditAble, translate.languageStore.currentLanguage])

    const initialForm = async () => {
        const responseInfo = await Promise.all([
            committeeHook.getCommitteeByID(Number(params.id)),
            committeeHook.getExpertiseInfo(Number(params.id)),
        ])
        if (responseInfo[0]) {
            setCommitteeInfo(responseInfo[0])
        }
        if (responseInfo[1] && responseInfo[1]?.result) {
            setInitialEditAble({ ...responseInfo[1].result })
        }
    }

    const initLut = async () => {
        loadingHook.loadingOpen()
        await Promise.all([expertiseHook.getExpertisesLUT(), lutHook.getExpertiseFaculty()]).then((response) => {
            setInitExpertise(response[0])
            setInitFaculty(response[1])
        })
        loadingHook.loadingClose()
    }

    const handleSubmitForm = () => {
        saveRef.current?.openDialog()
    }

    const handleConfirmSubmit = async (formRef: FormApi<any, Partial<any>>) => {
        /** CALL SERVICE UPDATE */
        saveRef.current?.closeDialog()
        const formValues = formRef.getState().values
        const committeeID = committeeInfo?.['userID'] ? Number(committeeInfo?.['userID']) : null
        const params = {
            faculty: formValues.faculty ?? null,
            updatedUserID: Number(updatedUserId),
            expertList: formValues.expertList.map((data: TOptions) => ({ ...data, label: data.label ?? null })),
        }
        await committeeHook.updateCommitteeExperise(committeeID, params).then((data: ServiceResult<null>) => {
            data.status === 1 && successRef.current?.openDialog()
        })
    }

    const handleBack = (formRef: FormApi<any, Partial<any>>) => {
        if (formRef.getState().dirty) {
            cancelRef.current?.openDialog()
        } else {
            confirmCancel()
        }
    }

    const bindCancelRef = (func: Record<string, Function>) => {
        cancelRef.current = { ...func }
    }

    const bindSaveRef = (func: Record<string, Function>) => {
        saveRef.current = { ...func }
    }

    const bindSuccessRef = (func: Record<string, Function>) => {
        successRef.current = { ...func }
    }

    const confirmCancel = () => {
        cancelRef.current?.closeDialog()
        history.push(RoutePath.manageCommittees)
    }

    const handleAddExpertise = (formRef: FormApi<any, Partial<any>>) => {
        const expertiseState = formRef.getFieldState('expertise')
        if (expertiseState?.value) {
            formRef.mutators.push('expertList', expertiseState?.value)
            expertiseState?.change(null)
            setDisabledAdd(true)
        }
    }

    const handleChangeDisabledAdd = (formState: any) => {
        const currentValues = formState.values
        if (currentValues.expertise) {
            setDisabledAdd(false)
        } else {
            setDisabledAdd(true)
        }
    }

    const handleRemoveExpertise = (keyIndex: number, formRef: FormApi<any, Partial<any>>) => {
        formRef.mutators.remove('expertList', keyIndex)
    }

    const renderExpertiseObj = (id: string | number) => {
        return expertise?.find((data: TOptions) => `${data.value}` === `${id}`)?.label ?? '-'
    }

    return (
        <FinalForm
            onSubmit={handleSubmitForm}
            mutators={{
                ...arrayMutators,
            }}
            initialValues={{ ...initialEditableForm, ...initialValues }}
        >
            {({ handleSubmit, form, values }) => {
                formStateRef.current = form
                return (
                    <>
                        <Card>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <CustomHeader variant="h5">{translate.get('UM.USERGROUP.HEADER')}</CustomHeader>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="usergroup"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.USERGROUP'),
                                                placeholder: translate.get('UM.USERGROUP-ADD.USERGROUP'),
                                                size: 'small',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="irb"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.GROUP'),
                                                placeholder: translate.get('UM.USERGROUP-ADD.GROUP'),
                                                size: 'small',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MyCheckbox
                                            name="isBothIrb"
                                            label={translate.get('UM.USERGROUP-ADD.READ_BOTH_IRB')}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CustomHeader variant="h5">
                                            {translate.get('UM.USERGROUP-ADD.PROFILE')}
                                        </CustomHeader>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Radio
                                            name="cunet"
                                            labelGroup={translate.get('UM.USERGROUP-ADD.TYPE_ACCOUNT')}
                                            options={[
                                                { id: '1', name: 'CU-NET' },
                                                { id: '2', name: translate.get('UM.USERGROUP-ADD.EXTERNAL') },
                                            ]}
                                            requiredHalfWidth={true}
                                            disabled
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="username"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.EMAIL'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.EMAIL'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MyTextfield
                                            name="email"
                                            materialInputProps={{
                                                label: `${translate.get('UM.PROFILE.EMAIL')} (${translate.get(
                                                    'UM.PROFILE.FOR_NEW_ALERT',
                                                )})`,
                                                placeholder: `${translate.get('UM.PROFILE.EMAIL')}`,
                                                size: 'small',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={12} lg={4}>
                                        <MyTextfield
                                            name="position"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.POSITION'),
                                                placeholder: translate.get('UM.USERGROUP-ADD.POSITION'),
                                                size: 'small',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={6} lg={4}>
                                        <MyTextfield
                                            name="positionOtherTH"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.OTHER_DESCRIPTION_TH'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.OTHER_DESCRIPTION_TH'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={6} lg={4}>
                                        <MyTextfield
                                            name="positionOtherEN"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.OTHER_DESCRIPTION_EN'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.OTHER_DESCRIPTION_EN'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <MyCheckbox
                                            name="isPhD"
                                            label={translate.get('UM.USERGROUP-ADD.DOCTOR')}
                                            disabled
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={12} lg={4}>
                                        <MyTextfield
                                            name="title"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.NAME_TITLE'),
                                                placeholder: translate.get('UM.USERGROUP-ADD.NAME_TITLE'),
                                                size: 'small',
                                                disabled: true,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={6} lg={4}>
                                        <MyTextfield
                                            name="titleOtherTH"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.NAME_OTHER_TITLE_TH'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.NAME_OTHER_TITLE_TH'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={6} lg={4}>
                                        <MyTextfield
                                            name="titleOtherEN"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.NAME_OTHER_TITLE_EN'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.NAME_OTHER_TITLE_EN'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="nameTH"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.FULLNAME_TH'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.FULLNAME_TH'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="nameEN"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.FULLNAME_EN'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.FULLNAME_EN'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="homeTelephone"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.TELEPHONE_HOME'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.TELEPHONE_HOME'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MyTextfield
                                            name="mobilePhone"
                                            materialInputProps={{
                                                label: translate.get('UM.USERGROUP-ADD.TELEPHONE_MOBILE'),
                                                disabled: true,
                                                placeholder: translate.get('UM.USERGROUP-ADD.TELEPHONE_MOBILE'),
                                                size: 'small',
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {!isReadOnly ? (
                                    <>
                                        <pre />
                                        <Grid item xs={12}>
                                            <CustomHeader variant="h5">
                                                {translate.get('LABEL.COMMITTEE_BELONGS_FACULTY')}
                                            </CustomHeader>
                                        </Grid>
                                        <pre />
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs>
                                                    <Autocomplete
                                                        name="faculty"
                                                        materialInputProps={{
                                                            label: translate.get('LABEL.FACULTY'),
                                                            placeholder: translate.get('LABEL.PLEASE_SELECT'),
                                                        }}
                                                        options={expertiseFaculty ?? []}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <pre />
                                        <Grid item xs={12}>
                                            <CustomHeader variant="h5">
                                                {translate.get('LABEL.DEFINE_EXPERTISE')}
                                            </CustomHeader>
                                        </Grid>
                                        <pre />
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs>
                                                    <Autocomplete
                                                        name="expertise"
                                                        materialInputProps={{
                                                            label: translate.get('LABEL.EXPERTISE'),
                                                            placeholder: translate.get('LABEL.PLEASE_SELECT'),
                                                        }}
                                                        options={
                                                            expertise?.filter((data: TOptions) =>
                                                                values.expertList.find(
                                                                    (exData: TOptions) =>
                                                                        `${exData.value}` === `${data.value}`,
                                                                )
                                                                    ? false
                                                                    : true,
                                                            ) ?? []
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item style={{ paddingTop: '50px' }}>
                                                    <Button
                                                        size={'large'}
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleAddExpertise(form)}
                                                        disabled={disabledAdd}
                                                    >
                                                        {translate.get('BUTTON.ADD')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <FieldArray name="expertList">
                                            {({ fields }) => {
                                                return fields?.map((name, index) => (
                                                    <Paper variant="outlined" className={classes.item} key={name}>
                                                        <Grid
                                                            container
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Grid item>
                                                                <Typography variant="body2">
                                                                    {renderExpertiseObj(values.expertList[index].value)}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item>
                                                                <Button
                                                                    size="medium"
                                                                    variant="contained"
                                                                    onClick={() => handleRemoveExpertise(index, form)}
                                                                    color="secondary"
                                                                >
                                                                    {translate.get('BUTTON.DELETE')}
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                ))
                                            }}
                                        </FieldArray>
                                    </>
                                ) : null}
                            </CardContent>
                        </Card>
                        <pre />
                        <Grid container justify="flex-end" spacing={2}>
                            {isReadOnly ? (
                                <Grid item>
                                    <Button
                                        size={'large'}
                                        variant="outlined"
                                        onClick={() => handleBack(form)}
                                        color="primary"
                                    >
                                        {translate.get('BUTTON.BACK')}
                                    </Button>
                                </Grid>
                            ) : (
                                <>
                                    <Grid item>
                                        <Button
                                            size={'large'}
                                            variant="outlined"
                                            onClick={() => handleBack(form)}
                                            color="primary"
                                        >
                                            {translate.get('BUTTON.CANCEL')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            size={'large'}
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSubmit}
                                        >
                                            {translate.get('BUTTON.SAVE')}
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        <FormSpy onChange={handleChangeDisabledAdd}></FormSpy>
                        <Dialog
                            onRef={bindCancelRef}
                            type="alert-confirm"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.NEED_TO_CANCEL')}
                            onClickConfirm={confirmCancel}
                        />
                        <Dialog
                            onRef={bindSaveRef}
                            type="alert-confirm"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.NEED_TO_SAVE')}
                            onClickConfirm={() => handleConfirmSubmit(form)}
                        />
                        <Dialog
                            onRef={bindSuccessRef}
                            type="alert-message"
                            title={translate.get('ALERT.TITLE.NOTIFICATION')}
                            content={translate.get('ALERT.CONTENT.SAVE_SUCCESS')}
                            onAfterClose={confirmCancel}
                        />
                    </>
                )
            }}
        </FinalForm>
    )
}

export default CommitteeForm
